import React, {
    forwardRef,
    useMemo,
    useRef,
    useEffect,
    useState,
    useImperativeHandle,
} from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { Table, Pagination, Select, Checkbox, Alert } from 'demo/components/ui'
import TableRowSkeleton from './loaders/TableRowSkeleton'
import Loading from './Loading'
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table'
import { useLocation } from 'react-router-dom'

const { Tr, Th, Td, THead, TBody, Sorter } = Table

/**
 * Did this click land on something the CELL owns?
 *
 * A cell can hold its own control - the document icon on a Geran column that opens the
 * file viewer, a link, a switch. Those do their own job, and the row must not also open
 * the record and navigate away from whatever was just opened.
 *
 * The `action` column is excluded wholesale by id; this catches the rest, wherever they
 * are. `.pointer` is this codebase's own marker for "this element is clickable" and is
 * on every such icon.
 *
 * NOT `label`. It is a form control everywhere else, but in these listings it is the
 * ordinary wrapper for two lines of text in a cell - 34 columns across the sales order
 * and consignment listings use it that way - so treating it as a control made Buyer,
 * Referred By, Paid at and every other two-line cell swallow the click and refuse to
 * open the record.
 */
const isOwnControl = (target) =>
    Boolean(
        target?.closest?.(
            'button, a, input, select, textarea, [role="button"], .pointer'
        )
    )

/**
 * The pair of scrollbars some report screens get above and below the table.
 *
 * DEFINED HERE, AT MODULE SCOPE, AND IT MATTERS. This used to live inside DataTable's
 * render body, which meant React saw a NEW COMPONENT TYPE on every render - a function
 * is only ever equal to itself - and a changed type is not an update, it is an unmount
 * and a remount. So every render threw the entire table away and built a new one.
 *
 * The visible cost was the horizontal scroll position. The table's `overflow-x-auto`
 * div is inside this wrapper, and a brand-new div starts at `scrollLeft: 0` - so any
 * re-render (a sort, a page, a refetch, a parent's state change) yanked a
 * side-scrolled listing back to its first column. Measured on the sales order list:
 * scrolled to 349px, clicked a sorter, and the node came back new at 0.
 *
 * It reads as "the columns keep changing while I scroll", and it reads worst on an
 * iPad with a trackpad, where a two-finger gesture is never purely vertical: the
 * table drifts sideways under the same scroll that moves the page, and every render
 * snaps it back. A mouse wheel scrolls one axis, which is why desktop browsers looked
 * fine.
 */
const TableScroll = ({ children, split, topRef, bottomRef, onSyncScroll }) => {
    if (!split) {
        return <>{children}</>
    }

    return (
        <div className="table-scroll-wrapper">
            <div
                className="table-scrollbar-top"
                ref={topRef}
                onScroll={onSyncScroll('top')}
            >
                {/* <div className="scrollbar-track" ref={topTrackRef}></div> */}
            </div>
            <div
                className="table-scrollbar-bottom"
                ref={bottomRef}
                onScroll={onSyncScroll('bottom')}
            >
                {children}
            </div>
        </div>
    )
}

const IndeterminateCheckbox = (props) => {
    const {
        indeterminate,
        onChange,
        onCheckBoxChange,
        onIndeterminateCheckBoxChange,
        ...rest
    } = props

    const ref = useRef(null)

    useEffect(() => {
        if (typeof indeterminate === 'boolean') {
            ref.current.indeterminate = !rest.checked && indeterminate
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, indeterminate])

    const handleChange = (e) => {
        onChange(e)
        onCheckBoxChange?.(e)
        onIndeterminateCheckBoxChange?.(e)
    }

    return (
        <Checkbox
            className="mb-0"
            ref={ref}
            onChange={(_, e) => handleChange(e)}
            {...rest}
        />
    )
}

const DataTable = forwardRef((props, ref) => {
    const {
        skeletonAvatarColumns,
        columns: columnsProp,
        data,
        loading,
        onCheckBoxChange,
        onIndeterminateCheckBoxChange,
        onPaginationChange,
        onSelectChange,
        onRowClick,
        onCellClick,
        onSort,
        pageSizes,
        selectable,
        skeletonAvatarProps,
        pagingData,
        textClassName,
        // Optional: (row) => a class for that row's <tr>, for a listing that marks
        // some of its rows. A listing that does not pass it is drawn as before.
        rowClassName,
    } = props

    const tableRef = useRef(null)
    const topScrollRef = useRef(null)
    const bottomScrollRef = useRef(null)
    const scrollPositionRef = useRef(0)

    const { pathname } = useLocation()

    const { pageSize, pageIndex, total } = pagingData

    const [sorting, setSorting] = useState(null)

    const pageSizeOption = useMemo(
        () =>
            pageSizes.map((number) => ({
                value: number,
                label: `${number} / page`,
            })),
        [pageSizes]
    )

    const handleCheckBoxChange = (checked, row) => {
        if (!loading) {
            onCheckBoxChange?.(checked, row)
        }
    }

    const handleIndeterminateCheckBoxChange = (checked, rows) => {
        if (!loading) {
            onIndeterminateCheckBoxChange?.(checked, rows)
        }
    }

    const handlePaginationChange = (page) => {
        if (!loading) {
            onPaginationChange?.(page)
        }
    }

    const handleSelectChange = (value) => {
        if (!loading) {
            onSelectChange?.(Number(value))
        }
    }

    useEffect(() => {
        if (Array.isArray(sorting)) {
            const sortOrder =
                sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : ''
            const id = sorting.length > 0 ? sorting[0].id : ''
            onSort?.({ order: sortOrder, key: id })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sorting])

    const hasOldColumnMetaKey = columnsProp.some(
        (col) => col.Header || col.accessor || col.Cell
    )

    const finalColumns = useMemo(() => {
        const columns = columnsProp

        if (selectable) {
            return [
                {
                    id: 'select',
                    header: ({ table }) => (
                        <IndeterminateCheckbox
                            checked={table.getIsAllRowsSelected()}
                            indeterminate={table.getIsSomeRowsSelected()}
                            onChange={table.getToggleAllRowsSelectedHandler()}
                            onIndeterminateCheckBoxChange={(e) => {
                                handleIndeterminateCheckBoxChange(
                                    e.target.checked,
                                    table.getRowModel().rows
                                )
                            }}
                        />
                    ),
                    cell: ({ row }) => (
                        <IndeterminateCheckbox
                            checked={row.getIsSelected()}
                            disabled={!row.getCanSelect()}
                            indeterminate={row.getIsSomeSelected()}
                            onChange={row.getToggleSelectedHandler()}
                            onCheckBoxChange={(e) =>
                                handleCheckBoxChange(
                                    e.target.checked,
                                    row.original
                                )
                            }
                        />
                    ),
                },
                ...columns,
            ]
        }
        return columns
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnsProp, selectable])

    const table = useReactTable({
        data,
        columns: hasOldColumnMetaKey ? [] : finalColumns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        manualPagination: true,
        manualSorting: true,
        onSortingChange: setSorting,
        state: {
            sorting,
        },
    })

    const resetSorting = () => {
        table.resetSorting()
    }

    const resetSelected = () => {
        table.toggleAllRowsSelected(false)
    }

    useImperativeHandle(ref, () => ({
        resetSorting,
        resetSelected,
    }))

    const tableMounted = tableRef?.current

    useEffect(() => {
        if (tableMounted && topScrollRef?.current) {
            const scrollWidth = tableRef?.current?.offsetWidth

            topScrollRef.current.style.setProperty(
                '--after-width',
                `${scrollWidth}px`
            )
        }
    }, [pathname, tableMounted])

    useEffect(() => {
        if (tableMounted && topScrollRef?.current && bottomScrollRef?.current) {
            const scrollLeft = scrollPositionRef.current

            topScrollRef.current.scrollLeft = scrollLeft
            bottomScrollRef.current.scrollLeft = scrollLeft
        }
    }, [tableMounted])

    if (hasOldColumnMetaKey) {
        const message =
            'You are using old react-table v7 column config, please use v8 column config instead, refer to our demo or https://tanstack.com/table/v8'

        if (process.env.NODE_ENV === 'development') {
            console.warn(message)
        }

        return <Alert>{message}</Alert>
    }

    //scroll bar
    const syncScroll = (from) => (e) => {
        const target = e.target
        const scrollLeft = target.scrollLeft
        scrollPositionRef.current = scrollLeft

        if (from === 'top' && bottomScrollRef.current) {
            bottomScrollRef.current.scrollLeft = scrollLeft
        } else if (from === 'bottom' && topScrollRef.current) {
            topScrollRef.current.scrollLeft = scrollLeft
        }
    }

    const scrollPageList = [
        '/report/order/list',
        '/jobsheet/list',
        '/report/commission/list',
    ]

    return (
        <>
            {/* <Loading loading={loading && data.length !== 0} type="cover"> */}
            <TableScroll
                split={scrollPageList.includes(pathname)}
                topRef={topScrollRef}
                bottomRef={bottomScrollRef}
                onSyncScroll={syncScroll}
            >
                <Table
                    className="listing-table"
                    ref={tableRef}
                    oveerflow={!scrollPageList.includes(pathname)}
                >
                    <THead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <Th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                        >
                                            {header.isPlaceholder ? null : (
                                                <div
                                                    className={classNames(
                                                        header.column.getCanSort() &&
                                                            'cursor-pointer select-none point',
                                                        loading &&
                                                            'pointer-events-none',
                                                        'text-center'
                                                    )}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    {flexRender(
                                                        header.column.columnDef
                                                            .header,
                                                        header.getContext()
                                                    )}
                                                    {header.column.getCanSort() && (
                                                        <Sorter
                                                            sort={header.column.getIsSorted()}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </Th>
                                    )
                                })}
                            </Tr>
                        ))}
                    </THead>
                    {loading ? (
                        <TableRowSkeleton
                            columns={finalColumns}
                            rows={pagingData.pageSize}
                            avatarInColumns={skeletonAvatarColumns}
                            avatarProps={skeletonAvatarProps}
                        />
                    ) : (
                        <TBody>
                            {table.getRowModel().rows.length > 0 ? (
                                table
                                    .getRowModel()
                                    .rows.slice(0, pageSize)
                                    .map((row) => {
                                        return (
                                            // `pinned` on a row tints it, so the five
                                            // cars on the homepage read as a group
                                            // rather than as "whatever happens to be at
                                            // the top". Any listing whose rows do not
                                            // carry the field is unaffected.
                                            <Tr
                                                key={row.id}
                                                className={[
                                                    row.original?.pinned
                                                        ? 'row-pinned'
                                                        : '',
                                                    typeof rowClassName ===
                                                    'function'
                                                        ? rowClassName(
                                                              row.original
                                                          )
                                                        : '',
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                            >
                                                {row
                                                    .getVisibleCells()
                                                    .map((cell) => {
                                                        return cell.column.columnDef.id?.includes(
                                                            'with-condition'
                                                        ) &&
                                                            row.original
                                                                .status ===
                                                                'cancelled' ? (
                                                            ''
                                                        ) : (
                                                            <Td
                                                                colspan={
                                                                    cell.column
                                                                        .columnDef
                                                                        .id ===
                                                                    'colspan-4'
                                                                        ? 4
                                                                        : ''
                                                                }
                                                                className={`${
                                                                    cell.column
                                                                        .columnDef
                                                                        .id ===
                                                                    'colspan-4'
                                                                        ? 'text-left'
                                                                        : textClassName
                                                                        ? textClassName
                                                                        : 'text-center'
                                                                } ${
                                                                    row.original
                                                                        .status ===
                                                                    'cancelled'
                                                                        ? 'text-red-600 bg-red-50'
                                                                        : ''
                                                                } ${
                                                                    (onRowClick ||
                                                                        onCellClick) &&
                                                                    cell.column
                                                                        .id !==
                                                                        'action'
                                                                        ? 'cell-opens-record'
                                                                        : ''
                                                                }`}
                                                                style={{
                                                                    // Only where the
                                                                    // cell actually
                                                                    // opens the record.
                                                                    // The action column
                                                                    // does not, and a
                                                                    // hand over a dead
                                                                    // cell says it does.
                                                                    cursor:
                                                                        (onRowClick ||
                                                                            onCellClick) &&
                                                                        cell.column
                                                                            .id !==
                                                                            'action'
                                                                            ? 'pointer'
                                                                            : '',
                                                                    minWidth: `${cell.column.columnDef.minSize}px`,
                                                                    maxWidth: `${cell.column.columnDef.maxSize}px`,
                                                                    padding:
                                                                        cell
                                                                            .column
                                                                            .columnDef
                                                                            .padding,
                                                                }}
                                                                key={cell.id}
                                                                onClick={(
                                                                    event
                                                                ) =>
                                                                    cell.column
                                                                        .id !==
                                                                        'action' &&
                                                                    cell.column
                                                                        .id !==
                                                                        'colspan-4' &&
                                                                    !isOwnControl(
                                                                        event.target
                                                                    )
                                                                        ? onCellClick
                                                                            ? onCellClick(
                                                                                  row.original,
                                                                                  cell
                                                                              )
                                                                            : onRowClick
                                                                            ? onRowClick(
                                                                                  row
                                                                                      .original
                                                                                      .id,
                                                                                  row.original
                                                                              )
                                                                            : undefined
                                                                        : undefined
                                                                }
                                                            >
                                                                {flexRender(
                                                                    cell.column
                                                                        .columnDef
                                                                        .cell,
                                                                    cell.getContext()
                                                                )}
                                                            </Td>
                                                        )
                                                    })}
                                            </Tr>
                                        )
                                    })
                            ) : (
                                <Tr>
                                    <Td
                                        className="no-data"
                                        colSpan={finalColumns?.length}
                                    >
                                        No data found!
                                    </Td>
                                </Tr>
                            )}
                        </TBody>
                    )}
                </Table>
            </TableScroll>
            {table.getRowModel().rows.length > 0 && (
                <div className="flex items-center justify-between mt-4">
                    {pagingData && (
                        <Pagination
                            pageSize={pageSize}
                            currentPage={pageIndex}
                            total={total}
                            onChange={handlePaginationChange}
                        />
                    )}
                    {onSelectChange && (
                        <div style={{ minWidth: 130 }}>
                            <Select
                                size="sm"
                                menuPlacement="top"
                                isSearchable={false}
                                value={pageSizeOption.filter(
                                    (option) => option.value === pageSize
                                )}
                                options={pageSizeOption}
                                onChange={(option) =>
                                    handleSelectChange(option.value)
                                }
                            />
                        </div>
                    )}
                </div>
            )}
            {/* </Loading> */}
        </>
    )
})

DataTable.propTypes = {
    columns: PropTypes.array,
    data: PropTypes.array,
    loading: PropTypes.bool,
    onCheckBoxChange: PropTypes.func,
    onIndeterminateCheckBoxChange: PropTypes.func,
    onPaginationChange: PropTypes.func,
    onSelectChange: PropTypes.func,
    onSort: PropTypes.func,
    pageSizes: PropTypes.arrayOf(PropTypes.number),
    selectable: PropTypes.bool,
    skeletonAvatarColumns: PropTypes.arrayOf(PropTypes.number),
    skeletonAvatarProps: PropTypes.object,
    pagingData: PropTypes.shape({
        total: PropTypes.number,
        pageIndex: PropTypes.number,
        pageSize: PropTypes.number,
    }),
}

DataTable.defaultProps = {
    pageSizes: [5, 10, 25, 50],
    pagingData: {
        total: 0,
        pageIndex: 1,
        pageSize: 10,
    },
    data: [],
    columns: [],
    selectable: false,
    loading: false,
}

export default DataTable
